import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import { createTextFilter } from '../../filters/types/textFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/TableLink';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type TableColumnFormatSwitchportIntName<TRow> = {
  type: 'switchportIntName';
  field: KeyOfType<TRow, string | string[] | null>;
  macCountField?: KeyOfType<TRow, number>;
} & TableColumnFormatLink<TRow>;

type SwitchportIntNameColumnConfig<TRow, TDataType> = TableDataColumnConfig<
  TRow,
  TDataType
> &
  TableLinkColumnConfig<TRow>;

export const createSwitchportIntNameColumn: {
  <TRow>(
    config: SwitchportIntNameColumnConfig<TRow, string | null> & {
      array?: false;
      macCountField?: KeyOfType<TRow, number>;
    },
  ): TableColumnBase<TRow, TableColumnFormatSwitchportIntName<TRow>>;
  <TRow>(
    config: SwitchportIntNameColumnConfig<TRow, string[] | null> & {
      array: true;
      macCountField?: KeyOfType<TRow, number>;
    },
  ): TableColumnBase<TRow, TableColumnFormatSwitchportIntName<TRow>>;
} = <TRow>(
  config: SwitchportIntNameColumnConfig<TRow, string | string[] | null> & {
    macCountField?: KeyOfType<TRow, number>;
  },
): TableColumnBase<TRow, TableColumnFormatSwitchportIntName<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'switchportIntName',
    field: config.key,
    macCountField: config.macCountField,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
    config.macCountField,
  ),
  toCsvString: (row) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, string | string[] | null>(row, config.key),
      (v: string | null): string => v ?? '',
    ),
  filter: createDefaultFilter(config.filter, createTextFilter()),
});
